<script setup lang="ts">
type EnumData = App.Base.Data.EnumData;
type StudentUserData = App.Auth.Data.StudentUserData;

import type { Selectable } from '@/components/selectBox/selectBox';

import Alert from '@/components/alert/Alert.vue';
import DangerDialog from '@/components/dialog/DangerDialog.vue';
import Dialog from '@/components/dialog/Dialog.vue';
import FormDateInput from '@/components/formDateInput/FormDateInput.vue';
import FormListboxInput from '@/components/formListboxInput/FormListboxInput.vue';
import FormRows from '@/components/formRows/FormRows.vue';
import FormTextInput from '@/components/formTextInput/FormTextInput.vue';

import Check from '@/icons/line/check.svg';
import InfoCircle from '@/icons/line/info-circle.svg';

const props = defineProps<{
  ethnicities: EnumData[];
  genders: EnumData[];
  user: StudentUserData;
}>();

const { show, close, closeLocally } = useDialog();
const [isCancelDialogOpen, toggleCancelDialog] = useToggle(false);
const [isContactAdviserDialogOpen, toggleContactAdviserDialog] = useToggle(false);

const isDialogOpen = computed(
  () => !isCancelDialogOpen.value && !isContactAdviserDialogOpen.value && show.value
);

const form = useForm({
  url: route('student.profile.update'),
  method: 'PUT',
  fields: {
    preferred_name: props.user.preferred_name,
    gender: props.user.gender as Selectable<string> | null,
    ethnicity: props.user.ethnicity as Selectable<string> | null
  },
  transform: (fields) => ({
    ...fields,
    gender: fields.gender?.value,
    ethnicity: fields.ethnicity?.value
  }),
  hooks: {
    success() {
      close();
    }
  }
});

function handleFormSubmit() {
  form.submit();
}

function handleToggleCancelDialog() {
  if (form.isDirty) {
    toggleCancelDialog();
    return;
  }
  closeLocally();
}

function handleCancelDialogConfirm() {
  toggleCancelDialog();
  closeLocally();
}

function handleNavigateToAdviser() {
  router.navigate({ url: route('student.adviser.index') });
}

function handleContactAdviserClick() {
  if (form.isDirty) {
    toggleContactAdviserDialog();
    return;
  }
  handleNavigateToAdviser();
}

function handleContactAdviserDialogConfirm() {
  form.submitWith({
    hooks: {
      success() {
        close();
        // TODO: Update when route is available
        handleNavigateToAdviser();
      },
      after() {
        toggleContactAdviserDialog();
      }
    }
  });
}

function handleContactAdviserDialogCancel() {
  toggleContactAdviserDialog();
  handleNavigateToAdviser();
}
</script>


					<script lang="ts">
					import __hybridly_layout_0 from '~/resources/layouts/student-parent.vue';
					export default { layout: [__hybridly_layout_0] }
					</script>
					<template>
				
  <Dialog
    title="Profile Information"
    :confirmButtonIcon="Check"
    :cancelButtonDisabled="!form.isDirty"
    :isLoading="form.processing"
    :isOpen="isDialogOpen"
    cancelButtonLabel="Reset"
    confirmButtonLabel="Save changes"
    @onCancel="form.reset"
    @onClose="handleToggleCancelDialog"
    @onConfirm="handleFormSubmit"
  >
    <template #form>
      <FormRows>
        <FormTextInput
          :error="form.errors.preferred_name"
          :modelValue="form.fields.preferred_name"
          isOptional
          label="Preferred first name"
          name="preferred_name"
          @update:modelValue="(value) => (form.fields.preferred_name = value)"
        />
        <FormTextInput
          :modelValue="user.first_name + ' ' + user.middle_name + ' ' + user.last_name"
          isDisabled
          label="Full legal name"
          name="first_name"
        />
        <FormDateInput :modelValue="user.dob" isDisabled label="Date of birth" name="dob" />
        <Alert
          :icon="InfoCircle"
          color="secondary"
          title="Is your legal name or date of birth wrong?"
          variant="soft"
          hideClose
        >
          <p>
            <button
              type="button"
              class="font-inherit leading-inherit cursor-pointer border-none bg-transparent p-0 text-inherit underline hover:no-underline"
              @click="handleContactAdviserClick"
            >
              Contact your adviser
            </button>
            to make a correction.
          </p>
        </Alert>
        <FormListboxInput
          label="Gender"
          :error="form.errors.gender"
          :options="genders"
          :modelValue="form.fields.gender"
          @update:modelValue="(value) => (form.fields.gender = value)"
        />
        <FormListboxInput
          label="Ethnicity"
          :error="form.errors.ethnicity"
          :options="ethnicities"
          :modelValue="form.fields.ethnicity"
          @update:modelValue="(value) => (form.fields.ethnicity = value)"
        />
      </FormRows>
    </template>
  </Dialog>
  <DangerDialog
    title="Discard Changes?"
    message="Your changes will not be saved."
    :isOpen="isCancelDialogOpen"
    cancelButtonLabel="Stay on this page"
    confirmButtonLabel="Discard changes"
    isStacked
    @onCancel="toggleCancelDialog()"
    @onClose="toggleCancelDialog()"
    @onConfirm="handleCancelDialogConfirm"
  />
  <Dialog
    title="Save Changes First?"
    message="You have unsaved changes. Would you like to save them before contacting your adviser?"
    :confirmButtonIcon="Check"
    :isLoading="form.processing"
    :isOpen="isContactAdviserDialogOpen"
    cancelButtonLabel="Continue without saving"
    confirmButtonLabel="Save and continue"
    isStacked
    @onCancel="handleContactAdviserDialogCancel()"
    @onClose="toggleContactAdviserDialog()"
    @onConfirm="handleContactAdviserDialogConfirm"
  />
</template>
